import { CIListFilter, DatePicker, getLocaleDateFormat, MultiSelectDropdown, toLocalDate } from 'ccs-ui-components';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { compareArrays, FilterProps, StartingFilterData } from './Utils';

const JourneyListFilter: React.FC<FilterProps> = ({ onApply, data = undefined, allStatusList, onResetFilters }: FilterProps) => {
  // state variables
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [status, setStatus] = useState<string[]>();
  const [allStatusSelected, setAllStatusSelected] = useState(false);
  const [startingValues, setStartingValues] = useState<StartingFilterData>();
  const [applyEnabled, setApplyEnabled] = useState(false);
  const { t: translate } = useTranslation();
  const statusValues = useMemo(() => allStatusList.map((s) => s.value), [allStatusList]);

  /**
   * @method applyClickHandler
   * @description applies filter changes on apply button click
   */
  const applyClickHandler = () => {
    onApply({
      startDate: startDate ? toLocalDate({ date: startDate, dateTimeFormat: { date: true }, targetDateFormat: 'YYYY-MM-DD' }) : undefined,
      endDate: endDate ? toLocalDate({ date: endDate, dateTimeFormat: { date: true }, targetDateFormat: 'YYYY-MM-DD' }) : undefined,
      status: status?.length ? status : statusValues,
    });
  };

  /**
   * @method selectAllHandler
   * @description select all status values
   * @param selected
   */
  const selectAllHandler = (selected: boolean) => {
    setStatus(selected ? statusValues : []);
    setAllStatusSelected(selected);
  };

  /**
   * @method clearAllHandler
   * @description Call the reset filters function
   */
  const clearAllHandler = () => {
    onResetFilters();
  };

  useEffect(() => {
    const sDate = data?.startDate ? new Date(data.startDate) : undefined;
    const eDate = data?.endDate ? new Date(data.endDate) : undefined;
    const statusList = data?.status?.length ? data.status : statusValues;
    setStartDate(sDate);
    setEndDate(eDate);
    setStatus(statusList);

    const copiedData: StartingFilterData = {
      startDate: sDate,
      endDate: eDate,
      status: statusList,
    };
    setAllStatusSelected(copiedData.status.length === statusValues.length);
    setStartingValues(copiedData);
  }, [data]);

  useEffect(() => {
    setApplyEnabled(
      startingValues?.startDate !== startDate
      || startingValues?.endDate !== endDate
      || !compareArrays(startingValues?.status ?? [], status ?? [])
    );
  }, [startDate, endDate, status, startingValues]);

  return (
    <CIListFilter applyEnabled={applyEnabled} onClear={clearAllHandler} onApply={applyClickHandler}>
      <Row>
        <Col xs={12} md={6}>
          <DatePicker
            labelText={String(translate('journey_list_component.txt_last_modified_start_date'))}
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date ?? undefined)}
            maxDate={endDate ?? new Date()}
            placeholderText={String(translate('journey_list_component.txt_date_plchd'))}
            dateFormat={getLocaleDateFormat()}
            data-testid="ciListFilterStartDate"
          />
        </Col>
        <Col xs={12} md={6}>
          <DatePicker
            labelText={String(translate('journey_list_component.txt_last_modified_end_date'))}
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date ?? undefined)}
            maxDate={new Date()}
            minDate={startDate}
            placeholderText={String(translate('journey_list_component.txt_date_plchd'))}
            dateFormat={getLocaleDateFormat()}
            data-testid="ciListFilterEndDate"
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <MultiSelectDropdown
            labelText={String(translate('journey_list_component.lbl_status'))}
            options={allStatusList}
            onSelect={(options) => setStatus(options)}
            handleSelectAll={selectAllHandler}
            selectAll={allStatusSelected}
            selectedOptions={status ?? statusValues}
            height="38px"
            data-testid="ciListStatusSelect"
          />
        </Col>
      </Row>
    </CIListFilter>
  );
};
export default JourneyListFilter;
